/**
 * 模型映射管理（管理员）。
 *
 * 管理 对外模型 → 上游实际模型 + 计费倍率 的映射关系 CRUD。
 *
 * @module pages/admin/ModelMappingAdminPage
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import {
  createModelMapping,
  deleteModelMapping,
  listChannels,
  listModelMappings,
  updateModelMapping,
  type ModelMappingInput,
} from '@/services/admin';
import type { ModelMapping } from '@/types';

/** 表单状态。 */
interface MappingForm {
  model: string;
  channelId: string;
  actualModel: string;
  multiplier: string;
  priority: string;
}

/** 空表单。 */
const EMPTY_FORM: MappingForm = {
  model: '',
  channelId: '',
  actualModel: '',
  multiplier: '1.00',
  priority: '10',
};

/**
 * 模型映射管理页组件。
 *
 * @returns 页面元素。
 */
export default function ModelMappingAdminPage(): JSX.Element {
  const { data: mappings, loading, refetch } = useApi(() => listModelMappings());
  const { data: channels } = useApi(() => listChannels());
  const { show, Toast } = useToast();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ModelMapping | null>(null);
  const [form, setForm] = useState<MappingForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const channelMap = useMemo(() => {
    const map = new Map<number, string>();
    (channels ?? []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [channels]);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (m: ModelMapping): void => {
    setEditing(m);
    setForm({
      model: m.model,
      channelId: String(m.channelId),
      actualModel: m.actualModel,
      multiplier: String(m.multiplier),
      priority: String(m.priority),
    });
    setOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    const channelId = Number(form.channelId);
    if (!form.model.trim() || !form.actualModel.trim() || !channelId) {
      show('请完整填写模型/实际模型/渠道', 'warning');
      return;
    }
    const input: ModelMappingInput = {
      model: form.model.trim(),
      channelId,
      actualModel: form.actualModel.trim(),
      multiplier: Number(form.multiplier) || 1,
      priority: Number(form.priority) || 0,
    };
    setSaving(true);
    try {
      if (editing) {
        await updateModelMapping(editing.id, input);
        show('映射已更新');
      } else {
        await createModelMapping(input);
        show('映射已创建');
      }
      setOpen(false);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await deleteModelMapping(deleteId);
      show('映射已删除');
      setDeleteId(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<ModelMapping>[] = [
    { key: 'model', header: '对外模型' },
    {
      key: 'channelId',
      header: '渠道',
      render: (m) => channelMap.get(m.channelId) ?? `#${m.channelId}`,
    },
    { key: 'actualModel', header: '上游实际模型' },
    {
      key: 'multiplier',
      header: '倍率',
      align: 'right',
      render: (m) => m.multiplier.toFixed(2),
    },
    { key: 'priority', header: '优先级', align: 'right', render: (m) => m.priority },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (m) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="编辑">
            <IconButton size="small" onClick={() => openEdit(m)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton size="small" color="error" onClick={() => setDeleteId(m.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="模型映射"
        subtitle="配置对外模型到上游实际模型的路由与计费倍率"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            新建映射
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={mappings ?? []}
        rowKey={(m) => m.id}
        loading={loading}
        emptyText="暂无模型映射"
      />

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '编辑映射' : '新建映射'}</DialogTitle>
        <DialogContent>
          <TextField label="对外模型名" fullWidth margin="normal" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="如 gpt-4o" />
          <TextField
            label="渠道"
            select
            fullWidth
            margin="normal"
            value={form.channelId}
            onChange={(e) => setForm({ ...form, channelId: e.target.value })}
          >
            {(channels ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="上游实际模型名" fullWidth margin="normal" value={form.actualModel} onChange={(e) => setForm({ ...form, actualModel: e.target.value })} />
          <TextField label="计费倍率" type="number" fullWidth margin="normal" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: e.target.value })} />
          <TextField label="优先级" type="number" fullWidth margin="normal" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteId != null}
        danger
        title="删除映射"
        message="确定删除该模型映射？"
        confirmText="删除"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setDeleteId(null)}
      />
      {Toast}
    </Box>
  );
}
